import { MutableRefObject, useEffect, useRef, WheelEvent as ReactWheelEvent } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { FollowAlignmentCanvas } from "features/alignment";
import { ArcgisCanvas } from "features/arcgis";
import { AreaCanvas } from "features/area";
import ClashCanvas from "features/clash/clashCanvas";
import { HeightProfileCanvas } from "features/heightProfile";
import { ManholeCanvas } from "features/manhole";
import { MarkerSvgRenderer } from "features/markers/MarkerSvgRenderer";
import { HoverCanvas, MeasureCanvas } from "features/measure";
import { selectMeasureVisible } from "features/measure/measureSlice";
import { CrossSectionCanvas } from "features/orthoCam";
import { OutlineLaserCanvas, OutlineLaserGizmoCanvas } from "features/outlineLaser";
import { PointLineCanvas } from "features/pointLine";

import { SvgRenderer } from "./svgRenderer";

type RenderFn = (moved: boolean, idleFrame?: boolean) => void;

export function Engine2D({
    onMeasureUpdate,
    pointerPosRef,
    renderFnRef,
    svg,
    onWheelCapture,
}: {
    onMeasureUpdate?: () => void;
    pointerPosRef: MutableRefObject<Vec2>;
    renderFnRef: MutableRefObject<RenderFn | undefined>;
    svg: SVGSVGElement | null;
    onWheelCapture?: (e: ReactWheelEvent) => void;
}) {
    const measureRenderFn = useRef<RenderFn | undefined>();
    const outlineLaserRenderFn = useRef<RenderFn | undefined>();
    const outlineLaserGizmoRenderFn = useRef<RenderFn | undefined>();
    const hoverRenderFn = useRef<RenderFn | undefined>();
    const areaRenderFn = useRef<RenderFn | undefined>();
    const plRenderFn = useRef<RenderFn | undefined>();
    const followPathRenderFn = useRef<RenderFn | undefined>();
    const manholeRenderFn = useRef<RenderFn | undefined>();
    const crossSectionRenderFn = useRef<RenderFn | undefined>();
    const heightProfileRenferFn = useRef<RenderFn | undefined>();
    const arcgisRenderFn = useRef<RenderFn | undefined>();
    const clashRenderFn = useRef<RenderFn | undefined>();
    const svgRendererRenderFnRef = useRef<RenderFn | undefined>();
    const markerRendererRenderFnRef = useRef<RenderFn | undefined>();

    const measureVisible = useAppSelector(selectMeasureVisible);

    useEffect(() => {
        renderFnRef.current = render;
        return () => (renderFnRef.current = undefined);

        function render(moved: boolean, idleFrame?: boolean) {
            areaRenderFn?.current?.(moved);
            plRenderFn?.current?.(moved);
            followPathRenderFn?.current?.(moved, idleFrame);
            manholeRenderFn?.current?.(moved);
            crossSectionRenderFn?.current?.(moved);
            heightProfileRenferFn?.current?.(moved);
            outlineLaserRenderFn?.current?.(moved);
            outlineLaserGizmoRenderFn?.current?.(moved);
            measureRenderFn.current?.(moved);
            hoverRenderFn?.current?.(moved);
            arcgisRenderFn?.current?.(moved);
            clashRenderFn?.current?.(moved);
            svgRendererRenderFnRef?.current?.(moved);
            markerRendererRenderFnRef?.current?.(moved);
        }
    }, [renderFnRef]);

    return (
        <>
            {measureVisible && (
                <>
                    <MeasureCanvas onMeasureUpdate={onMeasureUpdate} renderFnRef={measureRenderFn} svg={svg} />
                    <OutlineLaserCanvas renderFnRef={outlineLaserRenderFn} svg={svg} />
                    <OutlineLaserGizmoCanvas renderFnRef={outlineLaserGizmoRenderFn} />
                    <PointLineCanvas renderFnRef={plRenderFn} svg={svg} />
                </>
            )}
            <AreaCanvas renderFnRef={areaRenderFn} svg={svg} />
            <FollowAlignmentCanvas renderFnRef={followPathRenderFn} pointerPosRef={pointerPosRef} svg={svg} />
            <ManholeCanvas renderFnRef={manholeRenderFn} />
            <CrossSectionCanvas renderFnRef={crossSectionRenderFn} />
            <HeightProfileCanvas renderFnRef={heightProfileRenferFn} />
            <HoverCanvas renderFnRef={hoverRenderFn} />
            <ArcgisCanvas renderFnRef={arcgisRenderFn} svg={svg} />
            <ClashCanvas renderFnRef={clashRenderFn} svg={svg} />
            <SvgRenderer renderFnRef={svgRendererRenderFnRef} />
            <MarkerSvgRenderer renderFnRef={markerRendererRenderFnRef} onWheelCapture={onWheelCapture} />
        </>
    );
}
